import {
  ArrowDownRight,
  ArrowUpRight,
  History,
  MessageSquare,
  Plus,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { dashboardService } from "../../services/dashboardService.js";
import { selectActionPlansItems } from "../../store/slices/actionPlansSlice.js";
import Card from "../card/index.jsx";
import CardJourney from "../cardJourney/index.jsx";
import * as S from "../dashboard/_shared.jsx";
import DriverCompactRow from "../driverCompactRow/index.jsx";
import Section from "../section/index.jsx";
import Tag from "../tag/index.jsx";
import DistBar from "../ui/DistBar.jsx";
import { compareJourneyStages } from "../../constants/journeyStages";
import { normalizeKey } from "../../utils/normalizeString";
import { formatPercent } from "../../utils/formatters";
import { journeyStageReference, metricIdDaEtapa } from "./journeyReference";
import "./style.scss";
import { useChartAnimation } from "../charts/printMode";
import { formatMeta } from "../../utils/meta";
import { driversEmDestaque } from "../../utils/driversEtapa";

export default function JourneyView({ ctx }) {
  const animarGraficos = useChartAnimation();
  const {
    C,
    F,
    JCFG,
    JICONS,
    PILLAR_COLORS,
    PeriodPill,
    Zonas,
    etSt,
    getPilarOf,
    moe,
  } = S;
  const { etapaSel, setEtapaSel, setModal, setPrefilledAcao, isPrint } = ctx;

  const journeyState = useSelector((state) => state.dashboard.journey);
  const actionPlanItems = useSelector(selectActionPlansItems);
  const journey = journeyState?.data?.data || [];

  const driverMetricsRaw =
    useSelector((state) => state.dashboard.driverMetrics)?.data?.data || [];

  const journeySelected = etapaSel
    ? journey.find((e) => e.name === etapaSel)
    : null;

  const selectedMetricId = metricIdDaEtapa(journey, etapaSel);

  // Todos os IDs de métricas da etapa selecionada (pode haver CSAT + CES + outros)
  const selectedMetricIds = etapaSel
    ? new Set(
        journey
          .filter((e) => e.name === etapaSel)
          .map((e) => e.id)
          .filter(Boolean),
      )
    : new Set();

  const [etapaVerbatins, setEtapaVerbatins] = useState([]);
  const [isLoadingEtapaVerbatins, setIsLoadingEtapaVerbatins] = useState(false);
  const [etapaVerbatinsError, setEtapaVerbatinsError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!etapaSel || !selectedMetricId) {
      setEtapaVerbatins([]);
      setIsLoadingEtapaVerbatins(false);
      setEtapaVerbatinsError(null);
      return;
    }

    setIsLoadingEtapaVerbatins(true);
    setEtapaVerbatinsError(null);

    dashboardService
      .getVerbatins({ fk_id_metric: selectedMetricId })
      .then((res) => {
        if (cancelled) return;
        setEtapaVerbatins(res?.data || []);
      })
      .catch((error) => {
        if (cancelled) return;
        setEtapaVerbatinsError(
          error?.message || "Erro ao buscar verbatins desta etapa",
        );
        setEtapaVerbatins([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingEtapaVerbatins(false);
      });

    return () => {
      cancelled = true;
    };
  }, [etapaSel, selectedMetricId]);

  const benchmarkingData =
    useSelector((state) => state.dashboard.benchmarking)?.data?.data || [];
  const cxGeralBenchmark = benchmarkingData.find(
    (d) => d.name === "Jornada CX Geral",
  );
  const cxJornada = cxGeralBenchmark?.value ?? 0;
  const cxMercado = cxGeralBenchmark?.unimed_system_avg ?? 0;

  const jorGraf = journey?.map((e) => ({
    name: e.name,
    full: e.name,
    score: e.value,
    // A linha da API traz a meta em target (e.meta não existe: o tooltip
    // mostrava "Meta %").
    ...journeyStageReference(e),
    tipo: e.category,
    color: etSt(e.name, e.value).color,
  }));

  const [verbatinsModal, setVerbatinsModal] = useState(null);

  // Ordem da jornada vem de constants/journeyStages.js: a lista local tinha
  // "Promoção da saúde" (s minúsculo) e jogava a etapa para o fim.
  const sortedJourney = [...journey].sort((a, b) =>
    compareJourneyStages(a.name, b.name),
  );

  const csatCesChartJSX = (
    <Section
      title="Score CSAT e CES por etapa (Top2box) com Zonas de Referência"
      action={
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            fontSize: 10,
            color: C.text4,
            flexWrap: "wrap",
          }}
        >
          {[
            ["≥80% Excelente", "#059669"],
            ["70% Muito Bom", "#16a34a"],
            ["60% Bom", "#0284c7"],
            ["50% Atenção", "#d97706"],
            ["40% Ruim", "#dc2626"],
            ["<40% Crítico", "#991b1b"],
          ].map(([l, c]) => (
            <span key={l} style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <div style={{ width: 9, height: 9, borderRadius: 2, background: c, opacity: 0.7 }} />
              {l}
            </span>
          ))}
          <span style={{ color: C.border2, margin: "0 2px" }}>|</span>
          {[
            [`Jornada CX (${cxJornada}%)`, C.p400],
            [`Sist. Unimed (${cxMercado}%)`, C.g300],
          ].map(([l, c]) => (
            <span key={l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <svg width="18" height="8" style={{ flexShrink: 0 }}>
                <line x1="0" y1="4" x2="18" y2="4" stroke={c} strokeWidth="1.5" strokeDasharray="4 2" />
              </svg>
              {l}
            </span>
          ))}
        </div>
      }
    >
      <div style={{ padding: "0 20px 18px" }}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={jorGraf} margin={{ top: 12, bottom: 28, left: 0, right: 16 }}>
            {Zonas({ tipo: "ces" })}
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={C.border} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.text2, fontWeight: 500 }} height={32} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: C.text4 }} tickFormatter={(v) => v + "%"} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !(payload && payload.length)) return null;
                const r = jorGraf.find((x) => x.name === label);
                return (
                  <div style={{ background: "rgba(28,28,30,.95)", borderRadius: 10, padding: "8px 12px", fontSize: 12 }}>
                    <div style={{ color: "rgba(255,255,255,.5)", fontSize: 11, marginBottom: 4 }}>
                      {(r && r.full) || label}
                    </div>
                    <div style={{ color: "#FFFFFF" }}>
                      {(r && r.tipo) === "ces" ? "CES" : "CSAT"}:{" "}
                      <b style={{ color: r && r.color }}>{r && r.score}%</b>
                    </div>
                    <div style={{ color: "rgba(255,255,255,.4)", fontSize: 10 }}>
                      Meta {formatMeta(r?.meta, "%")} · Sist. Unimed{" "}
                      {formatPercent(r?.sys)}
                    </div>
                  </div>
                );
              }}
            />
            <ReferenceLine y={cxJornada} stroke={C.p400} strokeDasharray="6 3" strokeWidth={1.5} />
            <ReferenceLine y={cxMercado} stroke={C.g300} strokeDasharray="5 3" strokeWidth={1} />
            <Bar isAnimationActive={animarGraficos} dataKey="score" radius={[6, 6, 0, 0]} maxBarSize={64}>
              {jorGraf.map((r, i) => (
                <Cell key={i} fill={r.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Section>
  );

  return (
    <div
      className="tabJourneyView"
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: C.text,
            letterSpacing: "-0.02em",
          }}
        >
          Indicadores por etapa da jornada
        </div>
      </div>
      <div
        style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}
      >
        {sortedJourney.map((e) => (
          <CardJourney
            element={e}
            key={e.name}
            sel={isPrint ? false : etapaSel === e.name}
            onSel={
              isPrint
                ? undefined
                : (name) => setEtapaSel(etapaSel === name ? null : name)
            }
          />
        ))}
      </div>
      {/* In print, render the CSAT/CES chart before expanded stages */}
      {isPrint && csatCesChartJSX}
      {(isPrint
        ? sortedJourney.map((e) => e.name)
        : etapaSel
          ? [etapaSel]
          : []
      ).map((activeEtapa) => {
        const etapaSel = activeEtapa;
        return (() => {
          const journeySelected = journey.find((e) => e.name === etapaSel);
          if (!journeySelected) return null;

          // Valores vêm da linha da API; JCFG só dá o tipo (CSAT/CES) da etapa.
          const cfg = JCFG[etapaSel];
          const value =
            journeySelected.value !== undefined ? journeySelected.value : 0;
          const prevValue =
            journeySelected.value_delta !== undefined
              ? Math.round(value - (journeySelected.value_delta || 0))
              : 0;
          // Meta e Sist. Unimed só da API — sem dado mostra "—", nunca os
          // valores fixos de JCFG (ver journeyReference.js).
          const { meta, sys } = journeyStageReference(journeySelected);
          const tipo = journeySelected.category || (cfg && cfg.tipo);
          const st = etSt(etapaSel, value);
          const isCes = tipo === "ces" || (cfg && cfg.tipo === "ces");
          const IconComp2 = JICONS[etapaSel];

          return (
            <Card
              key={activeEtapa}
              style={{
                border: `1.5px solid ${st.color}`,
                padding: "14px 18px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: "0 0 auto" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 6,
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 9,
                        background: isCes ? C.blueBg : C.greenBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {IconComp2 && (
                        <IconComp2
                          size={16}
                          color={isCes ? C.blue : C.g700}
                          strokeWidth={1.8}
                        />
                      )}
                    </div>
                    <h4
                      style={{
                        margin: 0,
                        fontSize: 14,
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {etapaSel}
                    </h4>
                    <Tag
                      label={isCes ? "CES" : "CSAT"}
                      color={isCes ? C.blue : C.g700}
                    />
                    <button
                      onClick={() => setEtapaSel(null)}
                      style={{
                        marginLeft: 6,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: C.text4,
                        display: "flex",
                        padding: 0,
                      }}
                    >
                      <X size={15} />
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 14, marginTop: 2 }}>
                    {[
                      ["Atual", `${value}%`, st.color],
                      ["Meta", formatMeta(meta, "%"), C.p500],
                      ["Ant.", `${prevValue || 0}%`, C.text4],
                      ["Sist. Unimed", formatPercent(sys), C.text4],
                    ].map(([l, v, c]) => (
                      <div key={l} style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text4,
                            marginBottom: 1,
                            letterSpacing: "0.02em",
                          }}
                        >
                          {l}
                        </div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: c,
                            letterSpacing: "-0.03em",
                            fontFamily: F.display,
                            lineHeight: 1.1,
                          }}
                        >
                          {v}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {isCes && journeySelected && (
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: C.text3,
                        textTransform: "uppercase",
                        marginBottom: 5,
                        letterSpacing: "0.04em",
                      }}
                    >
                      Distribuição CES
                    </div>
                    <DistBar
                      dist={
                        journeySelected.opcao_1 !== undefined
                          ? {
                              ef: Math.round(
                                ((journeySelected.opcao_5 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                              f: Math.round(
                                ((journeySelected.opcao_4 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                              n: Math.round(
                                ((journeySelected.opcao_3 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                              d: Math.round(
                                ((journeySelected.opcao_2 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                              ed: Math.round(
                                ((journeySelected.opcao_1 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                            }
                          : null
                      }
                      tipo="ces"
                    />
                    <div
                      style={{
                        marginTop: 5,
                        fontSize: 10,
                        color: C.blue,
                        lineHeight: 1.4,
                      }}
                    >
                      <b>Top 2 Box = {value}%</b> (Fácil + Extr. Fácil) · meta{" "}
                      {formatMeta(meta, "%")}
                    </div>
                  </div>
                )}

                {!isCes && journeySelected && (
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: C.text3,
                        textTransform: "uppercase",
                        marginBottom: 5,
                        letterSpacing: "0.04em",
                      }}
                    >
                      Distribuição CSAT
                    </div>
                    <DistBar
                      dist={
                        journeySelected.opcao_1 !== undefined
                          ? {
                              ms: Math.round(
                                ((journeySelected.opcao_5 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                              s: Math.round(
                                ((journeySelected.opcao_4 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                              r: Math.round(
                                ((journeySelected.opcao_3 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                              i: Math.round(
                                ((journeySelected.opcao_2 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                              mi: Math.round(
                                ((journeySelected.opcao_1 || 0) * 100) /
                                  Math.max(
                                    1,
                                    (journeySelected.opcao_1 || 0) +
                                      (journeySelected.opcao_2 || 0) +
                                      (journeySelected.opcao_3 || 0) +
                                      (journeySelected.opcao_4 || 0) +
                                      (journeySelected.opcao_5 || 0),
                                  ),
                              ),
                            }
                          : null
                      }
                      tipo="csat"
                    />
                    <div
                      style={{
                        marginTop: 5,
                        fontSize: 10,
                        color: C.g700,
                        lineHeight: 1.4,
                      }}
                    >
                      <b>CSAT = {value}%</b> (notas 4 + 5) · meta{" "}
                      {formatMeta(meta, "%")}
                    </div>
                  </div>
                )}
              </div>

              {/* === DRIVERS COMPACTOS + VERBATINS + HISTORICO + CTA === */}
              {(() => {
                const driversEtapa = Object.values(
                  driverMetricsRaw
                    .filter((d) => selectedMetricIds.has(d.fk_id_metric))
                    .reduce((acc, d) => {
                      if (!acc[d.name])
                        acc[d.name] = {
                          d: d.name,
                          n: 0,
                          pos: 0,
                          neu: 0,
                          neg: 0,
                          metricCategory: d.metric?.category || null,
                        };
                      acc[d.name].n += d.mentions_count || 0;
                      acc[d.name].pos += d.positive_count || 0;
                      acc[d.name].neu += d.neutral_count || 0;
                      acc[d.name].neg += d.negative_count || 0;
                      return acc;
                    }, {}),
                );

                const { positivos, negativos } =
                  driversEmDestaque(driversEtapa);

                const verbatinsBrutos = etapaVerbatins;

                // Nomes dos drivers sendo renderizados (os de maior volume de cada lado)
                const driversRenderizados = new Set([
                  ...positivos.map((d) => d.d),
                  ...negativos.map((d) => d.d),
                ]);

                // Mapa normalizado para matching tolerante a acento/case/espaço
                const normStr = normalizeKey;
                const driversNorm = new Map(
                  [...driversRenderizados].map((name) => [normStr(name), name]),
                );

                // Transformar verbatins da API para formato compatível
                // Só inclui verbatims cujo driver está nos drivers renderizados
                // Usa driver_name quando preenchido; cai em topic para verbatims legados
                const verbatinsAll = verbatinsBrutos
                  .map((v) => {
                    const tipo =
                      v.type === "elogio"
                        ? "promotor"
                        : v.type === "reclamação"
                          ? "detrator"
                          : "neutro";
                    const texto = v.text || v.texto || "";
                    const rawDriver = v.driver_name || v.topic || "";
                    // Resolve para o nome canônico do driver (exato → normalizado → bruto)
                    const driver = driversRenderizados.has(rawDriver)
                      ? rawDriver
                      : (driversNorm.get(normStr(rawDriver)) ?? rawDriver);
                    return { ...v, tipo, texto, driver };
                  })
                  .filter((v) => v.driver && driversRenderizados.has(v.driver));

                // 1 reclamação por driver negativo (até 2) + 1 elogio por driver positivo (até 1)
                // Total max 3, pra caber compacto na coluna
                const usados = new Set();
                const pickVerbatim = (tipo, driverLabel) => {
                  const uid = (v) => v.id ?? v.texto;
                  // Match exato no driver solicitado
                  const especifico = verbatinsAll.find(
                    (v) =>
                      !usados.has(uid(v)) &&
                      v.driver === driverLabel &&
                      v.tipo === tipo,
                  );
                  // Fallback: qualquer verbatim do tipo certo cujo driver também está sendo renderizado
                  const fallback = verbatinsAll.find(
                    (v) =>
                      !usados.has(uid(v)) &&
                      v.tipo === tipo &&
                      driversRenderizados.has(v.driver),
                  );
                  const escolhido = especifico || fallback;
                  if (escolhido) usados.add(uid(escolhido));
                  return escolhido;
                };

                const reclamacoes = negativos
                  .slice(0, 2)
                  .map((dr) => pickVerbatim("detrator", dr.d))
                  .filter(Boolean);

                const elogios = positivos
                  .slice(0, 1)
                  .map((dr) => pickVerbatim("promotor", dr.d))
                  .filter(Boolean);

                const verbatinsParaMostrar = [...elogios, ...reclamacoes];
                const historico = actionPlanItems.filter(
                  (p) => p.etapa === etapaSel && p.status === "concluido",
                );

                // Pre-fill helpers
                const DRIVER_LABEL_TO_SLUG = {
                  "Resolutividade no 1º contato": "resolutividade",
                  "Agilidade no atendimento": "agilidade",
                  "Humanização no atendimento": "atendimento_humano",
                  "App facilitador": "acesso_digital",
                  "Atend. urgência e emergência": "rede_credenciada",
                  "Comunicação proativa": "comunicacao",
                  Reembolso: "reembolso",
                  "Conciliação (Concordância)": "cobertura",
                  "Proatividade da equipe": "prevencao",
                  "Dificuldade p/ agendar": "burocracia",
                };
                const metaEt = meta || 0;
                const gapEt = metaEt - value;

                const handleCriarPlano = () => {
                  const driversNeg = negativos
                    .map((d) => DRIVER_LABEL_TO_SLUG[d.d])
                    .filter(Boolean);
                  const severidade =
                    gapEt >= 20 ? "alta" : gapEt >= 10 ? "media" : "baixa";
                  const negLabels = negativos.map((d) => d.d).join(", ");
                  setPrefilledAcao({
                    titulo: `Melhorar ${etapaSel}`,
                    desc: negLabels
                      ? `Foco nos drivers críticos: ${negLabels}.`
                      : `Plano de ação focado em ${etapaSel}.`,
                    etapa: etapaSel,
                    tipo: isCes ? "ces" : "csat",
                    v_meta: metaEt,
                    prio: severidade,
                    drivers: driversNeg,
                  });
                  setModal("new");
                };

                return (
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: `1px dashed ${C.border}`,
                    }}
                  >
                    {/* 3 colunas: drivers + verbatins + histórico */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "minmax(0,1.1fr) minmax(0,1fr) minmax(0,1fr)",
                        gap: 10,
                        marginBottom: 10,
                      }}
                    >
                      {/* Drivers */}
                      <div
                        style={{
                          padding: "10px 12px",
                          background: C.surf3,
                          borderRadius: 9,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <Sparkles size={11} color={C.p500} /> por que está
                          assim
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text4,
                            fontStyle: "italic",
                            marginBottom: 8,
                          }}
                        >
                          drivers que mais influenciam
                        </div>

                        {positivos.length > 0 && (
                          <div>
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.green,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 4,
                                display: "flex",
                                alignItems: "center",
                                gap: 3,
                              }}
                            >
                              <ArrowUpRight size={9} /> mais ajudam
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                              }}
                            >
                              {positivos.map((dr) => (
                                <DriverCompactRow
                                  key={dr.d}
                                  driver={dr}
                                  isNeg={false}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {negativos.length > 0 && (
                          <div
                            style={{ marginBottom: positivos.length ? 8 : 0 }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.red,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 4,
                                display: "flex",
                                alignItems: "center",
                                gap: 3,
                              }}
                            >
                              <ArrowDownRight size={9} /> mais atrapalham
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                              }}
                            >
                              {negativos.map((dr) => (
                                <DriverCompactRow
                                  key={dr.d}
                                  driver={dr}
                                  isNeg={true}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {negativos.length === 0 && positivos.length === 0 && (
                          <div
                            style={{
                              fontSize: 10,
                              color: C.text4,
                              fontStyle: "italic",
                              padding: "4px 0",
                            }}
                          >
                            Nenhum driver com impacto significativo nesta etapa.
                          </div>
                        )}
                      </div>

                      {/* Verbatins */}
                      <div
                        style={{
                          padding: "10px 12px",
                          background: C.surf3,
                          borderRadius: 9,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <MessageSquare size={11} color={C.p500} /> o que dizem
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text4,
                            fontStyle: "italic",
                            marginBottom: 8,
                          }}
                        >
                          principais frases que explicam cada driver — clique
                          abaixo pra ver todas
                        </div>
                        {verbatinsParaMostrar.length === 0 ? (
                          <div
                            style={{
                              fontSize: 10,
                              color: C.text4,
                              fontStyle: "italic",
                              padding: "4px 0",
                            }}
                          >
                            Sem verbatins coletados nesta etapa.
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                            }}
                          >
                            {verbatinsParaMostrar.map((v, i) => {
                              const isNeg = v.tipo === "detrator";
                              const corDriver = isNeg
                                ? C.red
                                : v.tipo === "promotor"
                                  ? C.green
                                  : C.orange;
                              return (
                                <div
                                  key={i}
                                  style={{
                                    padding: "7px 9px",
                                    background: C.surface,
                                    borderRadius: 5,
                                    borderLeft: `3px solid ${corDriver}`,
                                    fontSize: 10,
                                    color: C.text2,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {/* Driver hint — qual driver este verbatim justifica */}
                                  <div
                                    style={{
                                      fontSize: 8,
                                      fontWeight: 700,
                                      color: corDriver,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.04em",
                                      marginBottom: 4,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 3,
                                    }}
                                  >
                                    {isNeg ? (
                                      <ArrowDownRight size={9} />
                                    ) : (
                                      <ArrowUpRight size={9} />
                                    )}
                                    {v.driver}
                                  </div>
                                  "{v.texto}"
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: C.text4,
                                      marginTop: 3,
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 4,
                                      alignItems: "center",
                                    }}
                                  >
                                    {v.persona}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Botão "Ver todos" — abre modal com todos os verbatins da etapa */}
                        {verbatinsAll.length > verbatinsParaMostrar.length && (
                          <button
                            onClick={() =>
                              setVerbatinsModal({
                                etapa: etapaSel,
                                drivers: { pos: positivos, neg: negativos },
                                verbatins: verbatinsAll,
                              })
                            }
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 5,
                              width: "100%",
                              marginTop: 8,
                              padding: "7px 10px",
                              background: "transparent",
                              border: `1px dashed ${C.border}`,
                              borderRadius: 6,
                              fontSize: 10,
                              fontWeight: 600,
                              color: C.text3,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              transition: "all .15s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = C.surface;
                              e.currentTarget.style.borderColor = C.p500;
                              e.currentTarget.style.color = C.p500;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                              e.currentTarget.style.borderColor = C.border;
                              e.currentTarget.style.color = C.text3;
                            }}
                          >
                            <MessageSquare size={10} />
                            Ver todas as {verbatinsAll.length} frases desta
                            etapa
                            <ArrowUpRight size={10} />
                          </button>
                        )}
                      </div>

                      {/* Histórico de ações */}
                      <div
                        style={{
                          padding: "10px 12px",
                          background: C.surf3,
                          borderRadius: 9,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <History size={11} color={C.p500} /> o que já tentaram
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text4,
                            fontStyle: "italic",
                            marginBottom: 8,
                          }}
                        >
                          planos com impacto medido
                        </div>
                        {historico.length === 0 ? (
                          <div
                            style={{
                              fontSize: 10,
                              color: C.text4,
                              fontStyle: "italic",
                              padding: "4px 0",
                            }}
                          >
                            Nenhuma ação concluída nesta etapa ainda. Será o
                            primeiro plano de ação aqui.
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 5,
                            }}
                          >
                            {historico.slice(0, 3).map((h, i) => (
                              <div
                                key={i}
                                style={{
                                  padding: "7px 9px",
                                  background: C.surface,
                                  borderRadius: 5,
                                  borderLeft: `3px solid ${C.green}`,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: C.text,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {h.titulo}
                                </div>
                                {h.prazo && (
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: C.text4,
                                      marginTop: 2,
                                    }}
                                  >
                                    prazo: {h.prazo}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* CTA centralizado */}
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <button
                        onClick={handleCriarPlano}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "9px 22px 9px 14px",
                          background: `linear-gradient(135deg, ${C.g700} 0%, ${C.g600} 100%)`,
                          color: "#FFFFFF",
                          border: "none",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          transition: "all .15s",
                          boxShadow: `0 3px 12px ${C.g700}40, 0 1px 3px rgba(0,0,0,.1)`,
                          letterSpacing: "-0.01em",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.boxShadow = `0 5px 16px ${C.g700}50, 0 2px 6px rgba(0,0,0,.12)`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = `0 3px 12px ${C.g700}40, 0 1px 3px rgba(0,0,0,.1)`;
                        }}
                      >
                        <div
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,.18)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Plus size={13} strokeWidth={2.5} />
                        </div>
                        <span>
                          Criar plano de ação para{" "}
                          <b style={{ fontWeight: 700 }}>{etapaSel}</b>
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })()}
            </Card>
          );
        })();
      })}

      {/* In screen mode, chart appears after expanded stages (original position) */}
      {!isPrint && csatCesChartJSX}

      {/* Modal de Verbatins — todas as frases da etapa selecionada */}
      {verbatinsModal &&
        (() => {
          const verbatinsRaw = verbatinsModal?.verbatins || [];

          // Transformar verbatins da API para formato compatível
          const verbatinsAll = verbatinsRaw.map((v) => {
            const tipo =
              v.type === "elogio"
                ? "promotor"
                : v.type === "reclamação"
                  ? "detrator"
                  : "neutro";
            const texto = v.text || v.texto || "";

            return {
              ...v,
              tipo,
              texto,
              driver: v.driver_name || v.topic || "",
            };
          });

          const reclamacoes = verbatinsAll.filter((v) => v.tipo === "detrator");
          const elogios = verbatinsAll.filter((v) => v.tipo === "promotor");
          const neutros = verbatinsAll.filter((v) => v.tipo === "neutro");
          // Driver labels que aparecem
          const driversUnicos = [...new Set(verbatinsAll.map((v) => v.driver))];

          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.55)",
                backdropFilter: "blur(6px)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 20,
              }}
            >
              <div
                style={{
                  background: C.surface,
                  borderRadius: 18,
                  maxWidth: 760,
                  width: "100%",
                  maxHeight: "90vh",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 24px 64px rgba(0,0,0,.3)",
                  overflow: "hidden",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "18px 22px",
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: `linear-gradient(135deg,${C.p100 || "#EDE9FE"} 0%,${C.surface} 100%)`,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        background: C.p500,
                        borderRadius: 11,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MessageSquare size={18} color="#FFFFFF" />
                    </div>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: 16,
                          fontWeight: 700,
                          color: C.text,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        O que os clientes dizem sobre{" "}
                        <span style={{ color: C.p500 }}>
                          {verbatinsModal.etapa}
                        </span>
                      </h3>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.text4,
                          marginTop: 2,
                          display: "flex",
                          gap: 10,
                        }}
                      >
                        <span>
                          <b style={{ color: C.red }}>{reclamacoes.length}</b>{" "}
                          reclamações
                        </span>
                        <span>
                          <b style={{ color: C.green }}>{elogios.length}</b>{" "}
                          elogios
                        </span>
                        {neutros.length > 0 && (
                          <span>
                            <b style={{ color: C.orange }}>{neutros.length}</b>{" "}
                            neutros
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setVerbatinsModal(null)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 6,
                      borderRadius: 6,
                      color: C.text4,
                      display: "flex",
                    }}
                  >
                    <XCircle size={22} />
                  </button>
                </div>

                {/* Lista scrollável */}
                <div
                  style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}
                >
                  {/* Elogios */}
                  {elogios.length > 0 && (
                    <div style={{ marginBottom: 18 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.green,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <ArrowUpRight size={13} /> Elogios ({elogios.length}) —
                        o que mais ajuda
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {elogios.map((v, i) => (
                          <div
                            key={i}
                            style={{
                              padding: "11px 14px",
                              background: C.surf3,
                              borderRadius: 9,
                              borderLeft: `3px solid ${C.green}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.green,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 5,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <ArrowUpRight size={10} /> {v.driver}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: C.text2,
                                lineHeight: 1.5,
                                fontStyle: "italic",
                              }}
                            >
                              "{v.texto}"
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: C.text4,
                                marginTop: 6,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                                alignItems: "center",
                              }}
                            >
                              {v.persona}
                              <span
                                style={{ marginLeft: "auto", color: C.text4 }}
                              >
                                {v.data}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reclamações */}
                  {reclamacoes.length > 0 && (
                    <div style={{ marginBottom: neutros.length > 0 ? 18 : 0 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.red,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <ArrowDownRight size={13} /> Reclamações (
                        {reclamacoes.length}) — o que mais atrapalha
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {reclamacoes.map((v, i) => (
                          <div
                            key={i}
                            style={{
                              padding: "11px 14px",
                              background: C.surf3,
                              borderRadius: 9,
                              borderLeft: `3px solid ${C.red}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.red,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 5,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <ArrowDownRight size={10} /> {v.driver}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: C.text2,
                                lineHeight: 1.5,
                                fontStyle: "italic",
                              }}
                            >
                              "{v.texto}"
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: C.text4,
                                marginTop: 6,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                                alignItems: "center",
                              }}
                            >
                              {v.persona}
                              <span
                                style={{ marginLeft: "auto", color: C.text4 }}
                              >
                                {v.data}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Neutros */}
                  {neutros.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.orange,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        Neutros ({neutros.length}) — observações sem polaridade
                        clara
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {neutros.map((v, i) => (
                          <div
                            key={i}
                            style={{
                              padding: "11px 14px",
                              background: C.surf3,
                              borderRadius: 9,
                              borderLeft: `3px solid ${C.orange}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.orange,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 5,
                              }}
                            >
                              {v.driver}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: C.text2,
                                lineHeight: 1.5,
                                fontStyle: "italic",
                              }}
                            >
                              "{v.texto}"
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: C.text4,
                                marginTop: 6,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                                alignItems: "center",
                              }}
                            >
                              {v.persona}
                              <span
                                style={{ marginLeft: "auto", color: C.text4 }}
                              >
                                {v.data}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {verbatinsAll.length === 0 && (
                    <div
                      style={{
                        padding: "30px 20px",
                        textAlign: "center",
                        color: C.text4,
                        fontSize: 13,
                        fontStyle: "italic",
                      }}
                    >
                      Sem verbatins coletados nesta etapa no período
                      selecionado.
                    </div>
                  )}
                </div>

                {/* Footer com nota */}
                <div
                  style={{
                    padding: "12px 22px",
                    borderTop: `1px solid ${C.border}`,
                    background: C.surf3,
                    fontSize: 10,
                    color: C.text4,
                    fontStyle: "italic",
                    lineHeight: 1.5,
                    flexShrink: 0,
                  }}
                >
                  ⓘ Pesquisa anônima · Verbatins extraídos automaticamente das
                  respostas abertas pelo Eva Engine, classificados por driver
                  via NLP
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
